/**
 * Parsers de `unknown` a los tipos del protocolo.
 *
 * Todos devuelven `null` en vez de lanzar: un registro corrupto en RTDB o un
 * mensaje raro por el DataChannel es una condición esperada, no una excepción, y
 * quien llama casi siempre quiere ignorar el dato y seguir vivo.
 */
import { MODIFIERS, } from './input.js';
import { SESSION_STATES, } from './signaling.js';
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function asString(value) {
    return typeof value === 'string' ? value : null;
}
function asNumber(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}
function asSessionState(value) {
    return typeof value === 'string' && SESSION_STATES.includes(value)
        ? value
        : null;
}
export function parseDeviceRecord(value) {
    if (!isRecord(value))
        return null;
    const ownerUid = asString(value['ownerUid']);
    const name = asString(value['name']);
    const platform = asString(value['platform']);
    const lastSeen = asNumber(value['lastSeen']);
    const online = value['online'];
    if (ownerUid === null || name === null || platform === null)
        return null;
    if (lastSeen === null || typeof online !== 'boolean')
        return null;
    return { ownerUid, name, online, lastSeen, platform };
}
export function parseSdpPayload(value) {
    if (!isRecord(value))
        return null;
    const type = asString(value['type']);
    const sdp = asString(value['sdp']);
    if (sdp === null || sdp.length === 0)
        return null;
    if (type !== 'offer' && type !== 'answer')
        return null;
    return { type, sdp };
}
export function parseIceCandidatePayload(value) {
    if (!isRecord(value))
        return null;
    const candidate = asString(value['candidate']);
    if (candidate === null)
        return null;
    const payload = { candidate };
    const sdpMid = asString(value['sdpMid']);
    if (sdpMid !== null)
        payload.sdpMid = sdpMid;
    const sdpMLineIndex = asNumber(value['sdpMLineIndex']);
    if (sdpMLineIndex !== null)
        payload.sdpMLineIndex = sdpMLineIndex;
    const usernameFragment = asString(value['usernameFragment']);
    if (usernameFragment !== null)
        payload.usernameFragment = usernameFragment;
    return payload;
}
export function parseSessionRecord(value) {
    if (!isRecord(value))
        return null;
    const deviceId = asString(value['deviceId']);
    const clientUid = asString(value['clientUid']);
    const createdAt = asNumber(value['createdAt']);
    const state = asSessionState(value['state']);
    if (deviceId === null || clientUid === null || createdAt === null || state === null) {
        return null;
    }
    const record = { deviceId, clientUid, createdAt, state };
    const offer = parseSdpPayload(value['offer']);
    if (offer !== null && offer.type === 'offer')
        record.offer = offer;
    const answer = parseSdpPayload(value['answer']);
    if (answer !== null && answer.type === 'answer')
        record.answer = answer;
    return record;
}
/**
 * Decodifica un mensaje del DataChannel.
 *
 * Acepta `unknown` porque `MessageEvent.data` puede ser un `ArrayBuffer` o un
 * `Blob` si la otra punta se confunde; solo tratamos strings y descartamos el resto.
 */
export function parseControlMessage(raw) {
    if (typeof raw !== 'string')
        return null;
    let decoded;
    try {
        decoded = JSON.parse(raw);
    }
    catch {
        return null;
    }
    if (!isRecord(decoded))
        return null;
    const t = asString(decoded['t']);
    switch (t) {
        case 'ping':
        case 'pong': {
            const id = asNumber(decoded['id']);
            const ts = asNumber(decoded['ts']);
            return id !== null && ts !== null ? { t, id, ts } : null;
        }
        case 'screens': {
            const screens = parseScreenOptions(decoded['screens']);
            const activeId = asString(decoded['activeId']);
            return screens !== null && activeId !== null ? { t, screens, activeId } : null;
        }
        case 'select-screen': {
            const id = asString(decoded['id']);
            return id !== null && id.length > 0 && id.length <= 64 ? { t, id } : null;
        }
        default:
            return null;
    }
}
function parseScreenOptions(value) {
    if (!Array.isArray(value))
        return null;
    const result = [];
    for (const item of value) {
        if (!isRecord(item))
            return null;
        const id = asString(item['id']);
        const name = asString(item['name']);
        if (id === null || name === null)
            return null;
        result.push({ id, name });
    }
    return result;
}
/** 0–1, ambos incluidos. Fuera de rango se descarta el mensaje entero. */
function asUnit(value) {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1
        ? value
        : null;
}
function asMouseButton(value) {
    return value === 0 || value === 1 || value === 2 ? value : null;
}
function asModifiers(value) {
    if (!Array.isArray(value))
        return null;
    const result = [];
    for (const item of value) {
        if (typeof item !== 'string' || !MODIFIERS.includes(item)) {
            return null;
        }
        result.push(item);
    }
    return result;
}
/**
 * Decodifica un mensaje de input llegado por el DataChannel.
 *
 * Es la frontera de confianza del control remoto: aquí entra lo que manda la otra
 * punta y de aquí sale algo que se convertirá en un evento de sistema real. Nada
 * pasa sin validar forma, tipo y rango; cualquier duda devuelve null y el agente
 * lo ignora.
 */
export function parseInputMessage(raw) {
    if (typeof raw !== 'string')
        return null;
    let decoded;
    try {
        decoded = JSON.parse(raw);
    }
    catch {
        return null;
    }
    if (!isRecord(decoded))
        return null;
    const t = asString(decoded['t']);
    switch (t) {
        case 'move': {
            const x = asUnit(decoded['x']);
            const y = asUnit(decoded['y']);
            return x !== null && y !== null ? { t, x, y } : null;
        }
        case 'down':
        case 'up': {
            const b = asMouseButton(decoded['b']);
            const x = asUnit(decoded['x']);
            const y = asUnit(decoded['y']);
            return b !== null && x !== null && y !== null ? { t, b, x, y } : null;
        }
        case 'wheel': {
            const dx = asNumber(decoded['dx']);
            const dy = asNumber(decoded['dy']);
            return dx !== null && dy !== null ? { t, dx, dy } : null;
        }
        case 'key': {
            const code = asString(decoded['code']);
            const down = decoded['down'];
            const mods = asModifiers(decoded['mods']);
            if (code === null || typeof down !== 'boolean' || mods === null)
                return null;
            // Un code de longitud desbocada no tiene sentido y no va a estar en la tabla;
            // se acota por higiene antes de que llegue al agente.
            if (code.length === 0 || code.length > 32)
                return null;
            return { t, code, down, mods };
        }
        case 'text': {
            const text = asString(decoded['text']);
            // Se acota a un fragmento razonable: el teclado móvil manda caracteres o
            // palabras sueltas, no párrafos. Evita que un mensaje gigante llegue al helper.
            if (text === null || text.length === 0 || text.length > 256)
                return null;
            return { t, text };
        }
        default:
            return null;
    }
}
//# sourceMappingURL=guards.js.map